import { PairedSalesStudy } from '@aloft/toolkit-shared';
import { Icon, Typography } from '@aloft/toolkit-sui';
import {
  AbsoluteCenter,
  Flex,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@aloft/toolkit-sui/chakra';

import { useNavigate } from 'react-router';

import { useStudiesState } from '../../../controller/hooks/useStudiesState.js';
import { selectDeviceWidth } from '../../../redux/features/selectors/uiSelectors.js';
import { useAppSelector } from '../../../redux/hooks.js';
import { ExportStudiesButton } from '../export/ExportStudiesButton.js';
import { createStudyResultString } from '../utils/createStudyResultString.js';
import { AddStudyModal } from './AddStudyModal.js';
import { StudyCard } from './StudyCard.js';

export const StudyList = ({ studies }: { studies: PairedSalesStudy[] }) => {
  const addStudyModalProps = useDisclosure();
  const navigate = useNavigate();
  const deviceWidth = useAppSelector(selectDeviceWidth);

  const { selectedStudies, setSelectedStudies } = useStudiesState();

  const handleCheckboxClick = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedStudies([...selectedStudies, id]);
    } else {
      setSelectedStudies(selectedStudies.filter((studyId) => studyId !== id));
    }
  };

  const handleEditClick = (id: string) => {
    navigate(`./paired-sales/${id}`);
  };

  return (
    <Flex height="100%" width="100%" direction="column" p={6} gap={4} overflowY="auto">
      {deviceWidth !== 'phone' && (
        <div className="add-study-button">
          <Tooltip
            defaultIsOpen={studies.length === 0}
            label="Click to create a new study"
            placement="bottom-end"
            hasArrow
          >
            <IconButton
              id="create-new-order-btn"
              data-testid="create-new-order-btn"
              aria-label="add order"
              icon={<Icon icon="add" size="lg" />}
              variant="primary"
              size="md"
              isRound
              left={1}
              zIndex={2}
              onClick={() => addStudyModalProps.onOpen()}
            />
          </Tooltip>
        </div>
      )}
      <ExportStudiesButton studies={studies} selectedStudies={selectedStudies} />
      {studies.length === 0 ? (
        <AbsoluteCenter>
          <Typography variant="h2">No studies yet</Typography>
        </AbsoluteCenter>
      ) : (
        studies.map((study) => (
          <StudyCard
            key={study.id}
            id={study.id}
            title={createStudyResultString(study.input.analysisFeature, study.body.reconciledValue)}
            feature={study.input.analysisFeature}
            createdAt={study.createdAt}
            isSelected={selectedStudies.includes(study.id)}
            pairs={study.body.selectedPairs}
            reconciledValue={study.body.reconciledValue}
            notes={study.body.notes}
            onCheckboxClick={(e) => handleCheckboxClick(study.id, e.target.checked)}
            onEditClick={() => handleEditClick(study.id)}
          />
        ))
      )}
      <AddStudyModal modalProps={addStudyModalProps} />
    </Flex>
  );
};
